import { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AppShell,
  makeSsoAppNavigation,
  type NavItem,
  type SsoAppNavigation,
} from '@kawai/app-shell';
import {
  buildSsoSessionKey,
  resolveHeaderAppLinks,
  resolveManagementAppLinks,
  resolvePortalUrl,
  type SsoSessionInput,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { TenantBrandContext, useTenantBrand } from '../lib/tenantBrand.js';
import { callIssueSsoToken } from '../lib/api/issueSsoToken.js';
import { getFeatureUrl } from '../lib/featureUrls.js';
import { ROUTES } from '../routes.js';

/**
 * S-90: tenant-portal の共通レイアウト。管理アプリと同じ `<AppShell>` を採用し、
 * ヘッダータブは先頭「ホーム」（現在地 / `isPortalCurrent`）+ 契約済み管理アプリで構成する。
 * 管理アプリと違い自 app は機能パッケージではないため `currentApp: null` で解決する。
 *
 * 認可ガードもここに集約する（従来は HomePage / BillingPage が個別に持っていた）:
 * - loading: 繋ぎ表示
 * - signed-out: /signin へ
 * - S-56: 純粋な SaaS 運営者（saas-admin かつ tenantId なし / features 空）は portal を
 *   経由させず saas-admin コンソールへ転送する。Layout 配下の全ページ（/billing 直アクセス
 *   含む）に適用される。自テナント兼任の saas-admin（saas_admin_tenant_dual_role）は対象外。
 */
export function Layout() {
  const { status, user, role, tenantId, features, readOnly, impersonatedBy, signOut } =
    useAuth();
  // S-10: テナントブランド（brandName / logoUrl / primaryColor）を sticky ヘッダに表示。
  // fetch はここ 1 回だけ。結果は TenantBrandContext で配下ページ（HomePage の
  // 契約情報 / お支払いカード）へ共有し、tenants doc の read 二重化を避ける。
  const tenantBrand = useTenantBrand(tenantId);
  const { brand } = tenantBrand;

  // S-91: 現セッション（uid + claims fingerprint）を毎 render で ref に反映。portal は
  // impersonation（readOnly / impersonatedBy）を持つため、それらもキーに含めて素リンク
  // 遷移・トークンキャッシュが impersonation 開始/終了を跨がないようにする。
  const sessionRef = useRef<SsoSessionInput>({
    uid: null,
    role: null,
    tenantId: null,
    features: [],
  });
  sessionRef.current = {
    uid: user?.uid ?? null,
    role,
    tenantId,
    features,
    readOnly,
    impersonatedBy,
  };
  const navRef = useRef<SsoAppNavigation | null>(null);
  if (navRef.current === null) {
    navRef.current = makeSsoAppNavigation(
      (targetApp, { prefetch }) => callIssueSsoToken({ targetApp, prefetch }),
      { getSessionKey: () => buildSsoSessionKey(sessionRef.current) },
    );
  }
  const ssoNavigation = navRef.current;

  const isSaasAdminOperatorOnly =
    role === 'saas-admin' && (tenantId === null || features.length === 0);

  // useEffect で外部遷移するため、初回 render で素通り（繋ぎ表示）→ 遷移実行する。
  // impersonation セッションでは role が tenant-staff なのでこの分岐には来ない。
  useEffect(() => {
    if (status === 'signed-in' && isSaasAdminOperatorOnly) {
      window.location.href = getFeatureUrl('saas-admin');
    }
  }, [status, isSaasAdminOperatorOnly]);

  // S-91 第2弾: 素リンク遷移先からの bounce（?sso_retry=<feature>）を受けて、
  // トークン付きハンドオーバーを自動でやり直す（確立記録は onSsoRetryApp が破棄）。
  useEffect(() => {
    if (status !== 'signed-in') return;
    const url = new URL(window.location.href);
    const retry = url.searchParams.get('sso_retry');
    if (!retry) return;
    url.searchParams.delete('sso_retry');
    window.history.replaceState({}, '', url.toString());
    const link = resolveManagementAppLinks(features, null, import.meta.env.MODE).find(
      (l) => l.feature === retry,
    );
    if (link) ssoNavigation.onSsoRetryApp(link);
  }, [status, features, ssoNavigation]);

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="ui-v2 flex min-h-full items-center justify-center text-body text-ink-3"
      >
        読み込み中…
      </div>
    );
  }

  if (status === 'signed-out') {
    return <Navigate to={ROUTES.signIn} replace />;
  }

  if (isSaasAdminOperatorOnly) {
    // useEffect でリダイレクトするため、その間の繋ぎ表示
    return (
      <div
        role="status"
        aria-live="polite"
        className="ui-v2 flex min-h-full items-center justify-center text-body text-ink-3"
      >
        SaaS 管理画面に移動しています…
      </div>
    );
  }

  // お支払いはカード登録導線のため tenant-admin / 兼任 saas-admin のみ表示
  // （staff に導線を出さない従来のホームカードの表示条件を踏襲）。
  // N-15b: 通知先登録はテナント所属の全員に見せる（staff は閲覧のみ / 編集ボタンはページ側で抑止）。
  const navItems: readonly NavItem[] = [
    { to: ROUTES.home, label: 'ホーム', end: true },
    ...((role === 'tenant-admin' || role === 'saas-admin') && tenantId
      ? [{ to: ROUTES.billing, label: 'お支払い' }]
      : []),
    ...(tenantId ? [{ to: ROUTES.notificationRecipients, label: '通知先登録' }] : []),
  ];

  // M-164: 「イベント」（promo の /event/status）を顧客/販促の右に並べる。
  const appLinks = resolveHeaderAppLinks(features, null, import.meta.env.MODE);

  return (
    <TenantBrandContext.Provider value={tenantBrand}>
      <AppShell
        navItems={navItems}
        brand={brand}
        appLinks={appLinks}
        onNavigateApp={ssoNavigation.onNavigateApp}
        onPrefetchApp={ssoNavigation.onPrefetchApp}
        prerenderUrls={ssoNavigation.selectPrerenderUrls(appLinks)}
        portalUrl={resolvePortalUrl(import.meta.env.MODE)}
        isPortalCurrent
        onSignOut={() => {
          ssoNavigation.clearAllEstablished();
          void signOut();
        }}
      />
    </TenantBrandContext.Provider>
  );
}
